import '../application';
import {analytics, Breakpoints, Carousel} from '@shopify/marketing-assets';

import WistiaModal from '../modules/wistia-modal';

const breakpoints = new Breakpoints();

/**
 * Initializes the hero carousel and handles the starting slide
 */
function initHero() {
  const heroSlideElements = document.querySelectorAll('.carousel-item');
  const carouselElement = document.querySelector('.hero-carousel');

  new Carousel(carouselElement, {
    currentIndex: getStartingMerchant(),
  });

  /**
   * Gets the starting slide based on the URL
   */
  function getStartingMerchant() {
    const desiredMerchant = carouselElement.dataset.desiredMerchant;

    if (!desiredMerchant) {
      return 0;
    }

    let desiredMerchantSlideIndex = 0;

    for (const [index, slide] of heroSlideElements.entries()) {
      if (slide.dataset.merchant === desiredMerchant) {
        desiredMerchantSlideIndex = index;
        break;
      }
    }

    return desiredMerchantSlideIndex;
  }

  carouselElement.addEventListener('slide-change', (event) => {
    analytics.track({
      eventCategory: 'hero-carousel',
      eventAction: 'slide-change',
      eventLabel: `slide-${event.detail + 1}`,
    });
  });
}

/**
 * Contains all functionality related to the merchant slider.
 * Each method first checks to see if the device is a small screen / phone. This is done to avoid
 * having to listen for resize and add / remove event listeners. Technically if a user is on a phone, the events will be assigned,
 * but I thought this was the better approach.
 */
class MerchantSlider {
  constructor() {
    this.sliderElement = document.querySelector('.merchant-slider__slides');
    this.slideElements = this.sliderElement.querySelectorAll('.merchant-slider__slide');
    this.numOfSlides = this.slideElements.length;
    this.dragAmountToTriggerChange = 10;
    this.draggingClass = 'dragging';
    this.slideElementsStartingX = 0;
    this.activeSlide = 0;
    this.prevButtonElement = document.querySelector('.merchant-slider__controls-button--prev');
    this.nextButtonElement = document.querySelector('.merchant-slider__controls-button--next');
    this.showMoreButtonElement = document.querySelector('.show-more-merchants');

    this.fauxScrollbarInnerElement = document.querySelector('.faux-scrollbar__inner');
    this.fauxScrollbarElement = document.querySelector('.faux-scrollbar');
    this.fauxScrollbarStartingX = 0;
  }

  /**
   * Sets the scrollbar size based on the number of slides and assigns functions
   */
  init() {
    this.fauxScrollbarInnerElement.style.width = `${100 / this.numOfSlides}%`;
    this.hideAndPreventTabbingForInactiveSlides();

    // assign functions for slider clicking and dragging
    this.sliderElement.addEventListener('mousedown', this.handleSliderMouseDownAndTouchStart.bind(this));
    this.sliderElement.addEventListener('touchstart', this.handleSliderMouseDownAndTouchStart.bind(this));
    this.sliderElement.addEventListener('mouseup', this.handleSliderMouseUpAndTouchEnd.bind(this));
    this.sliderElement.addEventListener('touchend', this.handleSliderMouseUpAndTouchEnd.bind(this));
    this.sliderElement.addEventListener('mouseleave', this.handleSliderMouseLeave.bind(this));
    this.sliderElement.addEventListener('mousemove', this.handleSliderMouseMove.bind(this));

    // assign function for buttons to navigate to next and prev slide
    this.prevButtonElement.addEventListener('click', () => {
      this.handleSliderButtonClicks();
    });
    this.nextButtonElement.addEventListener('click', () => {
      this.handleSliderButtonClicks(true);
    });

    // Mobile show more
    this.showMoreButtonElement.addEventListener('click', this.showMoreMerchantsOnMobile.bind(this));

    // assign functons for the scrollbar
    this.fauxScrollbarElement.addEventListener('mousedown', this.handleScrollbarMouseDownAndTouchStart.bind(this));
    this.fauxScrollbarElement.addEventListener('touchstart', this.handleScrollbarMouseDownAndTouchStart.bind(this));
    this.fauxScrollbarElement.addEventListener('mouseup', this.handleScrollbarMouseUpAndTouchEnd.bind(this));
    this.fauxScrollbarElement.addEventListener('touchend', this.handleScrollbarMouseUpAndTouchEnd.bind(this));
  }

  /**
   * Prevents the cursor from changing while dragging (Safari bug)
   * @param {Event} event The mouse move event
   */
  handleSliderMouseMove(event) {
    if (!this.dragging || breakpoints.matches('phone')) {
      return false;
    }

    event.preventDefault();

    return false;
  }

  /**
   * Handles mouseup touchstart events on the slider (Used for clicking and dragging to navigate)
   * @param {Event} event The click/touch event
   */
  handleSliderMouseDownAndTouchStart(event) {
    if (breakpoints.matches('phone')) {
      return false;
    }

    const isTouch = event.touches;

    if (!isTouch) {
      this.sliderElement.classList.add(this.draggingClass);
    }

    this.dragging = true;
    this.slideElementsStartingX = isTouch ? event.touches[0].clientX : event.pageX;

    return false;
  }

  /**
   * Handles mousedown touchend events on the slider (Used for clicking and dragging to navigate)
   * @param {Event} event The click/touch event
   */
  handleSliderMouseUpAndTouchEnd(event) {
    if (breakpoints.matches('phone')) {
      return false;
    }

    const isTouch = event.touches;

    if (!isTouch) {
      this.sliderElement.classList.remove(this.draggingClass);
    }

    const pageX = isTouch ? event.changedTouches[0].clientX : event.pageX;
    this.dragging = false;

    if (pageX - this.slideElementsStartingX <= -this.dragAmountToTriggerChange && this.activeSlide + 1 < this.numOfSlides) {
      // next slide
      this.changeSlide(this.activeSlide + 1);
    } else if (pageX - this.slideElementsStartingX >= this.dragAmountToTriggerChange && this.activeSlide - 1 >= 0) {
      // previous slide
      this.changeSlide(this.activeSlide - 1);
    }

    return false;
  }

  /**
   * Removes the dragging class (changes the cursor) from the slider if the user leaves the area
   * (Note: this is not needed for touch since the class is never added if the user is using touch)
   */
  handleSliderMouseLeave() {
    if (breakpoints.matches('phone')) {
      return false;
    }

    this.sliderElement.classList.remove(this.draggingClass);
    this.dragging = false;

    return false;
  }

  /**
   * Handles next and previous slide button clicks
   * @param {Boolean} next  Set to true if the user wants to see the next slide
   */
  handleSliderButtonClicks(next = false) {
    if (breakpoints.matches('phone')) {
      return false;
    }
    // go to next slide
    if (next && this.activeSlide + 1 < this.numOfSlides) {
      this.changeSlide(this.activeSlide + 1);
    } else if (!next && this.activeSlide - 1 >= 0) {
      this.changeSlide(this.activeSlide - 1);
    }

    return false;
  }

  /**
   * Handles mousedown and touchstart events on the scrollbar (Used for clicking to navigate)
   * @param {Event} event The click/touch event
   */
  handleScrollbarMouseDownAndTouchStart(event) {
    if (breakpoints.matches('phone')) {
      return false;
    }

    this.fauxScrollbarStartingX = event.touches ? event.touches[0].clientX : event.pageX;

    return false;
  }

  /**
   * Handles mouseup and touchend events on the scrollbar (Used for clicking to navigate)
   * @param {Event} event The click/touch event
   */
  handleScrollbarMouseUpAndTouchEnd(event) {
    const pageX = event.touches ? event.changedTouches[0].clientX : event.pageX;

    if (pageX - this.fauxScrollbarStartingX < this.dragAmountToTriggerChange && !breakpoints.matches('phone')) {
      this.changeSlidesWithScrollbarClicks(pageX);
    }

    return false;
  }

  /**
   * Changes the active slide
   * @param {Number} desiredSlide The slide that the user wishes to navigae to
   */
  changeSlide(desiredSlide = 0) {
    if (breakpoints.matches('phone')) {
      return false;
    }

    this.activeSlide = desiredSlide;

    this.enableAndDisableControls();
    this.hideAndPreventTabbingForInactiveSlides();
    this.sliderElement.style.transform = `translateX(-${this.activeSlide * 100}%)`;

    // for dragging, we may need to make this optional
    this.fauxScrollbarInnerElement.style.transform = `translateX(${this.activeSlide * 100}%)`;
    this.slideElements[this.activeSlide].focus();
    this.announceSlideChangeForScreenReaders();

    analytics.track({
      eventCategory: 'explore-merchants',
      eventAction: 'slide-change',
      eventLabel: `slide-${this.activeSlide + 1}`,
    });

    return false;
  }

  /**
   * Changes the slides to the correct slide based on where the user clicks and how many slides there are
   * @param {Number} pageX The X coordinate of the click / touch
   */
  changeSlidesWithScrollbarClicks(pageX) {
    const scrollbarWidth = this.fauxScrollbarElement.clientWidth;
    const sectionAmount = scrollbarWidth / this.numOfSlides;
    const clicked = Math.round(pageX - this.fauxScrollbarElement.getBoundingClientRect().left);
    let desiredSlide = 0;

    // Split the scrollbar into sections for clicking based on number of slides
    const waypoints = [...this.slideElements].map((slide, index) => {
      return {
        start: Math.round(index * sectionAmount),
        end: Math.round((index * sectionAmount) + sectionAmount),
      };
    });

    // find which slide the user wants
    for (const [index, waypoint] of waypoints.entries()) {
      if (clicked >= waypoint.start && clicked <= waypoint.end) {
        desiredSlide = index;
        break;
      }
    }

    this.changeSlide(desiredSlide);
  }

  /**
   * Sets a negative tabindex for merchant links in slides that are not active
   * also sets / removes the aria hidden
   */
  hideAndPreventTabbingForInactiveSlides() {
    if (breakpoints.matches('phone')) {
      return false;
    }

    [...this.slideElements].forEach((slide, index) => {
      if (index === this.activeSlide) {
        slide.removeAttribute('aria-hidden');

        [...slide.querySelectorAll('.merchant')].forEach((merchant) => {
          merchant.setAttribute('tabindex', '0');

          [...merchant.querySelectorAll('.merchant__button')].forEach((link) => {
            link.setAttribute('tabindex', '0');
          });
        });


      } else {
        slide.setAttribute('aria-hidden', true);

        [...slide.querySelectorAll('.merchant')].forEach((merchant) => {
          merchant.removeAttribute('tabindex');

          [...merchant.querySelectorAll('.merchant__button')].forEach((link) => {
            link.setAttribute('tabindex', '-1');
          });
        });
      }
    });

    return false;
  }

  /**
   * Enables/disabled the slider controls based on the current slide's position
   */
  enableAndDisableControls() {
    this.nextButtonElement.disabled = (this.activeSlide + 1 >= this.numOfSlides);
    this.prevButtonElement.disabled = (this.activeSlide - 1 < 0);
  }

  /**
   * Displays the next set of merchants on mobile
   */
  showMoreMerchantsOnMobile() {
    if (!breakpoints.matches('phone')) {
      return false;
    }

    const showClass = 'merchant-slider__slide--show';

    const hiddenSlides = [...this.slideElements].filter((slide) => !slide.classList.contains(showClass));

    if (hiddenSlides.length <= 1) {
      this.showMoreButtonElement.style.display = 'none';
    }

    hiddenSlides[0].classList.add(showClass);
    this.announceSlideChangeForScreenReaders();
    hiddenSlides[0].querySelectorAll('.merchant__button')[0].focus();

    return false;
  }

  /**
   * Announces the slide change for screen readers
   */
  announceSlideChangeForScreenReaders() {
    const ariaAnnouncementElement = document.querySelector('[aria-live="merchant-slider__a11y"]');
    ariaAnnouncementElement.innerHTML = (`<p>${this.slideElements[this.activeSlide].dataset.a11yAnnouncement}</p>`);
  }
}

/**
 * Fires a tracking event when the merchant and/or type of ad are used in the URL.
 * If the ad location is specified, the params are removed from the URL
 * (Example: /powered-by-shopify/rollie-nation?location=ferry)
 */
function trackURL() {
  const carouselElement = document.querySelector('.hero-carousel');
  const merchant = carouselElement.dataset.desiredMerchant;

  const urlSearchParams = new URLSearchParams(window.location.search);
  const type = Object.fromEntries(urlSearchParams.entries()).location;

  // remove query params from the url
  if (type) {
    urlSearchParams.delete('location');
    window.history.replaceState({}, document.title, `?${urlSearchParams.toString()}`);
  }

  if (type && merchant) {
    analytics.track({
      eventCategory: 'landing-page',
      eventAction: 'scan-qr-code',
      eventLabel: `${merchant}-${type}`,
    });
  } else if (merchant && !type) {
    analytics.track({
      eventCategory: 'landing-page',
      eventAction: 'page-load',
      eventLabel: `${merchant}`,
    });
  } else if (!merchant && type) {
    analytics.track({
      eventCategory: 'landing-page',
      eventAction: 'scan-qr-code',
      eventLabel: `${type}`,
    });
  }
}

/**
 * Checks the URL to see if the user scanned a burst 1 ad
 * If so, scroll down to the explore merchants section
 */
function handleBurstOne() {
  const desiredMerchant = document.querySelector('.hero-carousel').dataset.desiredMerchant;
  const burst1 = ['maap', 'go-to-skin-care', 'who-gives-a-crap', 'frank-green'];

  if (burst1.includes(desiredMerchant)) {
    window.location.hash = 'ExploreMerchants';
  }
}

/**
 * Initializes the page
 */
function init() {
  const merchantSlider = new MerchantSlider();
  merchantSlider.init();
  initHero();
  trackURL();
  handleBurstOne();
  document.querySelectorAll('.js-wistia-modal').forEach(($el) => new WistiaModal($el));
}

// Initialize the page ocne the page is done loading
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

